/* Circle D — in-chat order status, with Front behind it.
 *
 * The launcher opens a panel with three views: a menu, an order lookup, and the
 * result. "Check order status" resolves the order HERE — build stage, production
 * date and tracking — instead of collecting three fields and opening a human
 * conversation, which is what the Front bot's version of this did and what the
 * whole exercise is meant to stop.
 *
 * Things worth knowing before changing this:
 *
 *  1. The lookup calls /apps/service/order-status?compact=1, a Shopify App
 *     Proxy path. Shopify HMAC-signs the request on the way through, and the
 *     app still requires the order number AND the email on the order to match,
 *     so this endpoint is no more permissive than the page it mirrors. Never
 *     point it at anything but a same-origin /apps/ path — a cross-origin fetch
 *     loses the proxy signature and would need its own auth.
 *
 *  2. The result JSON carries `fullUrl` with a short-lived signed token, so
 *     "View full details" opens the real page with nothing retyped and no email
 *     address in the URL. Use it verbatim; do not rebuild that link here.
 *
 *  3. Front is initialised with useDefaultLauncher:false and its bundle is NOT
 *     fetched until "Talk to a person" is clicked. FrontChat does not exist for
 *     most visitors — never assume it is there.
 *
 *  4. The status menu item is a real anchor to /pages/order-status. We
 *     preventDefault it; with JS broken it still navigates. Keep it that way.
 */
(function () {
  'use strict';

  var CHAT_ID = document.currentScript && document.currentScript.getAttribute('data-chat-id');
  var SEEN_KEY = 'cds-chat-started';

  var root = document.getElementById('cds-chat');
  if (!root || !CHAT_ID) return;

  var launcher = root.querySelector('[data-chat-launcher]');
  var panel = root.querySelector('[data-chat-panel]');
  var startBtn = root.querySelector('[data-chat-start]');
  var fallback = root.querySelector('[data-chat-fallback]');
  var statusLink = root.querySelector('[data-chat-status]');
  var form = root.querySelector('[data-chat-form]');
  var errBox = root.querySelector('[data-chat-error]');
  var resultBox = root.querySelector('[data-chat-result]');
  if (!launcher || !panel || !startBtn) return;

  var views = {};
  Array.prototype.forEach.call(root.querySelectorAll('[data-chat-view]'), function (v) {
    views[v.getAttribute('data-chat-view')] = v;
  });

  var frontState = 'idle'; // idle | loading | ready | failed

  function started() {
    try { return sessionStorage.getItem(SEEN_KEY) === '1'; } catch (e) { return false; }
  }
  function markStarted() {
    try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* private mode */ }
  }

  function show(name) {
    Object.keys(views).forEach(function (k) { views[k].hidden = k !== name; });
    panel.scrollTop = 0;
  }

  /* ---------- panel ---------- */

  function openPanel() {
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    // Focus the first control only when the launcher was reached by keyboard —
    // moving focus after a mouse click makes the panel feel like it stole the
    // page.
    if (root.classList.contains('kb')) {
      var first = panel.querySelector('a, button, input');
      if (first) first.focus();
    }
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('pointerdown', onOutside, true);
  }

  function closePanel(refocus) {
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown);
    document.removeEventListener('pointerdown', onOutside, true);
    if (refocus) launcher.focus();
  }

  function onKeydown(e) {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    // Escape backs out one level before closing: someone reading a result
    // should not lose the panel entirely on the first press.
    if (!views.menu.hidden) closePanel(true); else show('menu');
  }
  function onOutside(e) { if (!root.contains(e.target)) closePanel(false); }

  launcher.addEventListener('mousedown', function () { root.classList.remove('kb'); });
  launcher.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') root.classList.add('kb');
  });

  launcher.addEventListener('click', function () {
    if (started()) { openChat(); return; }          // mid-conversation: skip the menu
    if (panel.hidden) { show('menu'); openPanel(); } else closePanel(true);
  });

  Array.prototype.forEach.call(root.querySelectorAll('[data-chat-back]'), function (b) {
    b.addEventListener('click', function () { show('menu'); launcher.focus(); });
  });

  /* ---------- order status ---------- */

  if (statusLink && form) {
    statusLink.addEventListener('click', function (e) {
      e.preventDefault();                            // anchor stays the no-JS path
      show('form');
      var f = form.querySelector('input');
      if (f) f.focus();
    });
  }

  function setError(msg) {
    if (!errBox) return;
    errBox.textContent = msg || '';
    errBox.hidden = !msg;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** Draws the card. Everything rendered here comes from the compact endpoint,
   *  which derives it from the SAME step list the full page uses — so the card
   *  and the page a customer opens next cannot disagree. */
  function renderResult(d) {
    var steps = d.steps || [];
    var idx = typeof d.currentIndex === 'number' ? d.currentIndex : -1;

    var bar = idx < 0 ? '' :
      '<div class="cds-chat__bar">' + steps.map(function (_, i) {
        return '<i class="' + (i <= idx ? 'on' : '') + '"></i>';
      }).join('') + '</div>' +
      '<div class="cds-chat__steps">' + steps.map(function (s, i) {
        return '<span class="' + (i === idx ? 'cur' : i < idx ? 'done' : '') + '">' + esc(s) + '</span>';
      }).join('') + '</div>';

    var date = d.productionDate
      ? '<div class="cds-chat__date"><div class="k">Estimated Production Date</div>' +
        '<div class="v">' + esc(d.productionDate) + '</div></div>'
      : '';

    var ships = (d.shipments || []).map(function (s) {
      var links = (s.trackingUrls || []).length
        ? (s.tracking || []).map(function (t, i) {
            var u = s.trackingUrls[i];
            return u ? '<a href="' + esc(u) + '" target="_blank" rel="noopener">' + esc(t) + '</a>' : esc(t);
          }).join(', ')
        : (s.tracking || []).map(esc).join(', ');
      return '<div class="cds-chat__ship"><b>' + esc(s.carrier) + '</b>' +
        (s.date ? ' &middot; ' + esc(s.date) : '') +
        (links ? '<br>' + links : '') + '</div>';
    }).join('');

    resultBox.innerHTML =
      '<div class="cds-chat__ordno">Order ' + esc(d.orderName) + '</div>' +
      '<div class="cds-chat__head">' + esc(d.headline) + '</div>' +
      // In Production ships an empty blurb on purpose (see STEPS in
      // order-status-html.ts). Emitting the element anyway leaves its margin
      // under the headline, so skip it entirely when there is nothing to say.
      (d.blurb ? '<div class="cds-chat__blurb">' + esc(d.blurb) + '</div>' : '') +
      bar + date + ships +
      '<a class="cds-chat__full" href="' + esc(d.fullUrl) + '">View Full Details</a>' +
      '<button type="button" class="cds-chat__again" data-chat-again>Check Another Order</button>';

    var again = resultBox.querySelector('[data-chat-again]');
    if (again) {
      again.addEventListener('click', function () {
        setError('');
        show('form');
        var f = form && form.querySelector('input');
        if (f) f.focus();
      });
    }
    show('result');
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var order = (form.order && form.order.value || '').trim();
      var email = (form.email && form.email.value || '').trim();
      if (!order || !email) { setError('Enter your order number and the email on the order.'); return; }

      var submit = form.querySelector('[data-chat-submit]');
      setError('');
      if (submit) { submit.setAttribute('aria-busy', 'true'); submit.disabled = true; }

      var qs = 'order=' + encodeURIComponent(order) + '&email=' + encodeURIComponent(email) + '&compact=1';
      fetch('/apps/service/order-status?' + qs, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (d) {
          if (d && d.ok) { renderResult(d); return; }
          // A structured error from the app is a real answer (wrong number,
          // wrong email); anything else means we never got one.
          setError((d && d.error) || 'We could not check that order just now. Please try again.');
        })
        .catch(function () {
          setError('We could not reach our order system. Check your connection and try again.');
        })
        .then(function () {
          if (submit) { submit.removeAttribute('aria-busy'); submit.disabled = false; }
        });
    });
  }

  /* ---------- Front, loaded on demand ---------- */

  // ⚠️ FrontChat('init') is ASYNCHRONOUS. Calling 'show' on the next line
  // creates the iframe at 0x0 and never opens it — the bundle loads, the button
  // stops spinning, and nothing appears. onInitCompleted is the only signal
  // that the widget is ready, so every 'show' is queued behind it. Do not
  // "simplify" this back into a straight-line init/show pair.
  function initFront(done) {
    if (!window.FrontChat) { done(false); return; }
    var settled = false;
    var finish = function (ok) { if (!settled) { settled = true; done(ok); } };
    try {
      window.FrontChat('init', {
        chatId: CHAT_ID,
        useDefaultLauncher: false,
        onInitCompleted: function () { finish(true); },
      });
    } catch (e) { finish(false); return; }
    // Belt and braces: if Front ever stops firing the callback, do not leave the
    // visitor with a button that has already stopped looking busy.
    setTimeout(function () { finish(!!window.FrontChat); }, 6000);
  }

  function showFront() {
    try { window.FrontChat('show'); } catch (e) { /* nothing useful to do */ }
  }

  function failOpen() {
    frontState = 'failed';
    startBtn.removeAttribute('aria-busy');
    startBtn.textContent = 'Chat Unavailable Right Now';
    startBtn.disabled = true;
    if (fallback) fallback.hidden = false;
  }

  function openChat() {
    if (frontState === 'ready') { markStarted(); closePanel(false); showFront(); return; }
    if (frontState === 'loading') return;
    if (frontState === 'failed') { if (fallback) fallback.hidden = false; return; }

    frontState = 'loading';
    startBtn.setAttribute('aria-busy', 'true');

    var s = document.createElement('script');
    s.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
    // A stalled network is the case that matters: onerror never fires, so the
    // button would spin forever without this.
    var timer = setTimeout(function () { if (frontState === 'loading') failOpen(); }, 8000);
    s.onload = function () {
      clearTimeout(timer);
      initFront(function (ok) {
        if (!ok) { failOpen(); return; }
        frontState = 'ready';
        startBtn.removeAttribute('aria-busy');
        markStarted();
        closePanel(false);
        showFront();
      });
    };
    s.onerror = function () { clearTimeout(timer); failOpen(); };
    document.head.appendChild(s);
  }

  startBtn.addEventListener('click', openChat);

  // Someone already mid-conversation gets Front warmed on idle, so their next
  // click opens instantly instead of spinning.
  if (started()) {
    (window.requestIdleCallback || function (f) { setTimeout(f, 3000); })(function () {
      if (frontState !== 'idle') return;
      frontState = 'loading';
      var s = document.createElement('script');
      s.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
      s.onload = function () { initFront(function (ok) { frontState = ok ? 'ready' : 'idle'; }); };
      s.onerror = function () { frontState = 'idle'; };
      document.head.appendChild(s);
    });
  }
})();
